import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
} from '@mui/material';
import {
  compareAnalyticsDriftAsync,
  fetchAnalyticsDriftGroupedAsync,
  selectAnalyticsDrift,
  selectIgnoreList,
  setAnalyticsDriftShowIgnored,
} from '../../../redux/locatorDrift/locatorDriftSlice';
import { isBeaconIgnored } from '../../../utilities/locatorDrift/locatorFormatters';
import EmptyState from '../shared/EmptyState';
import MetricCard from '../shared/MetricCard';
import VersionSelect from '../shared/VersionSelect';
import AnalyticsTable from './AnalyticsTable';
import { analyticsCategories } from '../locatorDriftConstants';
import { analyticsDriftTabStyles, analyticsSummaryColors } from './analyticsDriftStyles';

export default function AnalyticsDriftTab() {
  const dispatch = useDispatch();
  const analyticsDrift = useSelector(selectAnalyticsDrift);
  const ignoreList = useSelector(selectIgnoreList);
  const [groupIndex, setGroupIndex] = useState('');
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);
  const grouped = analyticsDrift.grouped;
  const selectedGroup = groupIndex !== '' ? grouped[Number(groupIndex)] : null;
  const versions = selectedGroup?.versions ? Object.keys(selectedGroup.versions) : [];
  const comparison = analyticsDrift.comparison;
  const items = comparison?.items || [];

  useEffect(() => {
    dispatch(fetchAnalyticsDriftGroupedAsync());
  }, [dispatch]);

  const filteredCategories = analyticsCategories.map((category) => {
    let categoryItems = items.filter(
      (item) => (item.status || item.drift || '').toUpperCase() === category.key,
    );
    if (!analyticsDrift.showIgnored) {
      categoryItems = categoryItems.filter((item) => !isBeaconIgnored(ignoreList, item.beacon_id));
    }
    return { ...category, items: categoryItems };
  });

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={analyticsDriftTabStyles.controlsPaper}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Flow</InputLabel>
              <Select
                label="Flow"
                value={groupIndex}
                onChange={(event) => {
                  setGroupIndex(event.target.value);
                  setVersionA('');
                  setVersionB('');
                }}
              >
                {grouped.map((group, index) => (
                  <MenuItem
                    key={`${group.flow_id}-${group.scenario}-${index}`}
                    value={String(index)}
                  >
                    {group.flow_id} - {group.scenario} [{group.platform}]
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <VersionSelect
              label="Version A"
              versions={versions}
              value={versionA}
              onChange={setVersionA}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <VersionSelect
              label="Version B"
              versions={versions}
              value={versionB}
              onChange={setVersionB}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              disabled={
                !selectedGroup ||
                !versionA ||
                !versionB ||
                versionA === versionB ||
                analyticsDrift.comparing
              }
              onClick={() =>
                dispatch(
                  compareAnalyticsDriftAsync({
                    flow_id: selectedGroup.flow_id,
                    platform: selectedGroup.platform,
                    version_a: versionA,
                    version_b: versionB,
                  }),
                )
              }
              sx={analyticsDriftTabStyles.compareButton}
            >
              {analyticsDrift.comparing ? <CircularProgress size={20} /> : 'Compare'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {analyticsDrift.groupedError && <Alert severity="error">{analyticsDrift.groupedError}</Alert>}
      {analyticsDrift.error && <Alert severity="error">{analyticsDrift.error}</Alert>}
      {!comparison ? (
        <EmptyState>Select a flow and two versions to compare analytics drift.</EmptyState>
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <MetricCard
                label="Beacons Passed"
                value={comparison.summary?.passed || 0}
                color={analyticsSummaryColors.passed}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                label="Regressions"
                value={comparison.summary?.regressions || 0}
                color={analyticsSummaryColors.regressions}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                label="Other Changes"
                value={
                  (comparison.summary?.fixed || 0) +
                  (comparison.summary?.new || 0) +
                  (comparison.summary?.removed || 0) +
                  (comparison.summary?.failed_both || 0)
                }
                color={analyticsSummaryColors.otherChanges}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Switch
                checked={analyticsDrift.showIgnored}
                onChange={(event) => dispatch(setAnalyticsDriftShowIgnored(event.target.checked))}
              />
            }
            label="Show ignored"
          />
          {filteredCategories.every((category) => category.items.length === 0) ? (
            <EmptyState>No analytics drift items match the current filters.</EmptyState>
          ) : (
            filteredCategories
              .filter((category) => category.items.length > 0)
              .map((category) => (
                <AnalyticsTable
                  key={category.key}
                  category={category}
                  allItems={items}
                  expandedIndex={expandedIndex}
                  setExpandedIndex={setExpandedIndex}
                />
              ))
          )}
        </Stack>
      )}
    </Stack>
  );
}
